import { Link } from 'react-router-dom';
import {
  UXP_CR_ID,
  DASHBOARD_METRICS,
  ASSET_TYPES,
  DIRECTOR_OUTPUTS,
  HERO_FEATURES,
  LIFESTYLE_SCENES,
  INFOGRAPHIC_TYPES,
  PREMIUM_APLUS_TYPES,
  VIDEO_TYPES,
  BRAND_STORE_TYPES,
  VISUAL_DNA_DIMENSIONS,
  PROMPT_RULES,
  VALIDATION_CHECKS,
  EXPERIMENT_TYPES,
  BULK_OPERATIONS,
  COLLABORATION_FEATURES,
  ASSET_MGMT_FEATURES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  ANALYTICS_METRICS,
  LIVE_ROUTES,
  DEMO_DASHBOARD_HEALTH,
  DEMO_VALIDATION,
  DEMO_DIRECTOR_BRIEF,
  DEMO_VISUAL_DNA,
  DEMO_ASSET_COUNTS,
  buildCreativeStudioDashboard,
} from '../../creativestudio/framework';
import {
  CreativeDashboardPanel,
  ProductCreativeGrid,
  CreativeDirectorCard,
  HeroImageStudioPanel,
  LifestyleStudioPanel,
  VisualDNAEngineCard,
  QualityValidationPanel,
  BulkCreativeFactoryBar,
} from '../../components/creativestudio';
import { Card, KpiCard } from '../../components/ui';

export function CreativeStudioPlatformPage() {
  const dashboard = buildCreativeStudioDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_CR_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">AI Creative Studio</h1>
        <p className="mt-1 text-sm text-slate-500">
          Visual commerce intelligence, AI Creative Director, Visual DNA engine, quality validation, and bulk creative factory — the Brand Creative Operating System.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-700 hover:bg-purple-100 dark:border-purple-900 dark:bg-purple-950 dark:text-purple-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Asset Types" value={dashboard.asset_types.toString()} />
        <KpiCard label="Director Outputs" value={dashboard.director_outputs.toString()} />
        <KpiCard label="Visual DNA" value={dashboard.visual_dna_dimensions.toString()} />
        <KpiCard label="Validation Checks" value={dashboard.validation_checks.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Creative Dashboard</h2>
        <p className="mt-1 text-sm text-slate-500">{DASHBOARD_METRICS.join(' · ')}</p>
        <div className="mt-4">
          <CreativeDashboardPanel metrics={DEMO_DASHBOARD_HEALTH} />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Product Creative Workspace</h2>
        <Card className="mt-4" padding="sm">
          <ProductCreativeGrid assetTypes={ASSET_TYPES} counts={DEMO_ASSET_COUNTS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <CreativeDirectorCard brief={DEMO_DIRECTOR_BRIEF} />
        <VisualDNAEngineCard dna={DEMO_VISUAL_DNA} />
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <Card padding="sm">
          <HeroImageStudioPanel features={HERO_FEATURES} />
        </Card>
        <Card padding="sm">
          <LifestyleStudioPanel scenes={LIFESTYLE_SCENES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Quality Validation</h2>
        <p className="mt-1 text-sm text-slate-500">{VALIDATION_CHECKS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <QualityValidationPanel items={DEMO_VALIDATION} />
        </Card>
      </section>

      <section>
        <BulkCreativeFactoryBar operations={BULK_OPERATIONS} />
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Infographic Studio</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {INFOGRAPHIC_TYPES.map((t) => (
              <span key={t} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{t}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Premium A+ Studio</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {PREMIUM_APLUS_TYPES.map((t) => (
              <span key={t} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{t}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Video Studio</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {VIDEO_TYPES.map((t) => (
              <span key={t} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{t}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Brand Store Studio</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {BRAND_STORE_TYPES.map((t) => (
              <span key={t} className="rounded-lg border border-orange-200 bg-orange-50 px-2 py-1 text-xs text-orange-700 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300">{t}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Prompt Orchestration</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {PROMPT_RULES.map((r) => (
              <span key={r} className="rounded-lg border border-ai-200 bg-ai-50 px-2 py-1 text-xs text-ai-700 dark:border-ai-800 dark:bg-ai-950 dark:text-ai-300">{r}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">AI Creative Director Outputs</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {DIRECTOR_OUTPUTS.map((o) => (
              <li key={o}>✓ {o}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Experimentation</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {EXPERIMENT_TYPES.map((e) => (
              <li key={e}>✓ {e}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {COLLABORATION_FEATURES.map((f) => (
              <li key={f}>✓ {f}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Asset Management</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {ASSET_MGMT_FEATURES.map((f) => (
              <li key={f}>✓ {f}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Visual DNA Dimensions</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {VISUAL_DNA_DIMENSIONS.map((d) => (
              <span key={d} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{d}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <li key={s}>✓ {s}</li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Analytics Metrics</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ANALYTICS_METRICS.map((m) => (
            <span key={m} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{m}</span>
          ))}
        </div>
      </section>

      <p className="text-xs text-slate-500">
        Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/uxp/UXP-00006_AI_CREATIVE_STUDIO.md</code>
        {' · '}
        Registry: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/creativestudio/</code>
        {' · '}
        Validation: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/aicreative/validation.go</code>
      </p>
    </div>
  );
}
